import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useActiveConflictSummary } from '../hooks/useICalConflicts';

/**
 * App-wide, persistent indicator of active iCal double-booking/no-availability conflicts across
 * every apartment the signed-in owner manages (US4). Renders nothing when there are none; renders
 * a badge with the total count otherwise. React Query's background refetch of the underlying
 * per-apartment queries (`useActiveConflictSummary`) updates this without a full page reload
 * (FR-011). Clicking navigates to the apartment with the most active conflicts, landing on that
 * apartment's Conflicts tab (added in `Availabilities.tsx`).
 */
export const ActiveConflictIndicator = () => {
  const { t } = useTranslation(['pages']);
  const navigate = useNavigate();
  const { data } = useActiveConflictSummary();

  if (data.totalActiveCount === 0) {
    return null;
  }

  const targetApartmentId = Object.entries(data.byApartment).reduce<string | null>(
    (best, [apartmentId, count]) => {
      if (!best) return apartmentId;
      return count > (data.byApartment[best] ?? 0) ? apartmentId : best;
    },
    null
  );

  const handleClick = () => {
    if (!targetApartmentId) return;
    navigate(`/apartments/${targetApartmentId}/availabilities`, {
      state: { viewMode: 'conflicts' },
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={t('pages:activeConflictIndicatorLabel', { count: data.totalActiveCount })}
      data-testid="active-conflict-indicator"
      className="flex items-center gap-2 px-4 py-2 mx-4 mb-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors border border-red-200"
    >
      <AlertTriangle size={18} />
      <span className="text-sm font-semibold">{data.totalActiveCount}</span>
    </button>
  );
};
